import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, forkJoin, map, Observable, Subscription } from 'rxjs';

import { ResponsiveService } from 'src/app/services/responsive.service';
import { ScrollingService } from 'src/app/services/scrolling.service';
import { CompatibilityService } from '../../services/compatibility.service';
import { TranslateModule } from '@ngx-translate/core';
import { CompatibleMachinesComponent } from './compatible-machines/compatible-machines.component';
import { BrandListComponent } from './brand-list/brand-list.component';
import { EquipmentSelectionComponent } from './equipment-selection/equipment-selection.component';
import { EnvironmentSelectionComponent } from './environment-selection/environment-selection.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { StepSeparatorComponent } from '../../../../../../shared/components/step-separator/step-separator.component';
import { LazyImageDirective } from '../../../../../../directive/lazyImage.directive';

@Component({
    selector: 'app-compatibility-list',
    templateUrl: './compatibility-list.component.html',
    styleUrls: ['./compatibility-list.component.scss'],
    standalone: true,
    imports: [
        LazyImageDirective,
        StepSeparatorComponent,
        NgIf,
        EnvironmentSelectionComponent,
        EquipmentSelectionComponent,
        BrandListComponent,
        CompatibleMachinesComponent,
        AsyncPipe,
        TranslateModule,
    ],
})
export class CompatibilityListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _scrollingSubscription!: Subscription;
  private _scrollToCurrentStepIfTopPosition =
    this.scrollTopEventHandler.bind(this);
  isBrandListMode$: Observable<boolean>;
  currentStep$: Observable<number>;
  doesStep3Exist$: Observable<boolean>;
  doesStep4Exist$: Observable<boolean>;
  displayedComponents$: Observable<number[]>;

  constructor(
    private compatibilityService: CompatibilityService,
    private responsiveService: ResponsiveService,
    private scrollingService: ScrollingService
  ) {
    this.isBrandListMode$ = this.compatibilityService.selectedEnvironment$.pipe(
      map((env) => {
        return env === 'all';
      })
    );
    this.currentStep$ = this.compatibilityService.currentStep$;
    this.doesStep3Exist$ = combineLatest([
      this.isBrandListMode$,
      this.compatibilityService.isOpticalSelected$,
    ]).pipe(
      map(([isBrandListMode, isOpticalSelected]) => {
        return !(isBrandListMode && isOpticalSelected);
      })
    );
    this.doesStep4Exist$ = combineLatest([
      this.isBrandListMode$,
      this.compatibilityService.isOpticalSelected$,
      this.currentStep$,
    ]).pipe(
      map(([isBrandListMode, isOpticalSelected, currentStep]) => {
        return !(isBrandListMode || isOpticalSelected) && currentStep >= 3;
      })
    );
    this.displayedComponents$ = combineLatest([
      this.currentStep$,
      this.responsiveService.width$,
    ]).pipe(
      map(([currentStep, width]) => {
        const steps = [1, 2, 3, 4];
        const array = steps.filter((step) => {
          return currentStep === step || (currentStep > step && width > 500);
        });
        return array;
      })
    );
  }

  ngOnInit(): void {
    this._scrollingSubscription =
      this.compatibilityService.scrollToId$.subscribe((elementId) => {
        this.scrollingService.detectChangesAndScrollSmoothlyTo(elementId);
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (window.scrollY === 0) {
        this.scrollingService.detectChangesAndScrollSmoothlyTo(
          `compatibilityStep${this.compatibilityService.currentStep$.getValue()}`
        );
        return;
      }
      window.addEventListener('scroll', this._scrollToCurrentStepIfTopPosition);
    });
  }

  ngOnDestroy(): void {
    if (this._scrollingSubscription) {
      this._scrollingSubscription.unsubscribe();
    }
    window.removeEventListener(
      'scroll',
      this._scrollToCurrentStepIfTopPosition
    );
  }

  private scrollTopEventHandler() {
    if (window.scrollY === 0) {
      this.scrollingService.detectChangesAndScrollSmoothlyTo(
        `compatibilityStep${this.compatibilityService.currentStep$.getValue()}`
      );
      window.removeEventListener(
        'scroll',
        this._scrollToCurrentStepIfTopPosition
      );
    }
  }
}
